// PendingTasks.js
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  Chip,
  useTheme,
  Button,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import {
  Assignment,
  ArrowForward,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useAuth } from "../../hooks/useAuth";
import {
  TASK_STATUS,
  TASK_PRIORITY_COLORS,
} from "../../utils/models/taskModel";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";

const PendingTasks = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: tasks, loading } = useCollection("tasks");
  const { update } = useAuditFirestore("tasks");
  

  const myPendingTasks = useMemo(() => {
    if (!tasks) return [];
    return tasks
      .filter(
        (task) =>
          task.isActive !== false &&
          (Array.isArray(task.assignedTo)
            ? task.assignedTo.includes(user.uid)
            : task.assignedTo === user.uid) &&
          task.status !== TASK_STATUS.COMPLETED &&
          task.status !== TASK_STATUS.CANCELLED,
      )
      .sort((a, b) => {
        if (a.dueDate && b.dueDate)
          return new Date(a.dueDate) - new Date(b.dueDate);
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      })
      .slice(0, 5);
  }, [tasks, user.uid]);

  const completedCount = useMemo(() => {
    if (!tasks) return 0;
    return tasks.filter(
      (t) =>
        (Array.isArray(t.assignedTo)
          ? t.assignedTo.includes(user.uid)
          : t.assignedTo === user.uid) && t.status === TASK_STATUS.COMPLETED,
    ).length;
  }, [tasks, user.uid]);

  const totalCount = useMemo(() => {
    if (!tasks) return 0;
    return tasks.filter(
      (t) =>
        (Array.isArray(t.assignedTo)
          ? t.assignedTo.includes(user.uid)
          : t.assignedTo === user.uid) && t.isActive !== false,
    ).length;
  }, [tasks, user.uid]);

  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const isOverdue = (task) => {
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date();
  };

  const handleStatusToggle = async (task, event) => {
    event.stopPropagation();
    const newStatus = TASK_STATUS.COMPLETED;

    await update(task.id, {
      status: newStatus,
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Card
      sx={{
        height: "100%",
        borderTop: 4,
        borderColor: "#00e1d2",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[8],
          transform: { xs: "none", sm: "translateY(-4px)" },
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            mb: { xs: 1.5, sm: 2, md: 2.5 },
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              background: "linear-gradient(135deg, #00afa3 0%, #00e1d2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Assignment sx={{ color: "white", fontSize: { xs: 20, sm: 24 } }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" } }}
            >
              Mis Tareas Pendientes
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            >
              {myPendingTasks.length} pendiente
              {myPendingTasks.length !== 1 ? "s" : ""} · {completedCount}{" "}
              completada{completedCount !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>

        {totalCount > 0 && (
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              >
                Progreso
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              >
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: { xs: 6, sm: 8 }, borderRadius: 4 }}
            />
          </Box>
        )}

        {myPendingTasks.length === 0 ? (
          <EmptyState
            title="Sin tareas pendientes"
            description="No tienes tareas asignadas"
            compact
          />
        ) : (
          <List dense disablePadding>
            {myPendingTasks.map((task) => (
              <ListItem key={task.id} disablePadding sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: "100%",
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.12)"
                        : "#e2e8f0",
                    borderLeft: 4,
                    borderLeftColor: isOverdue(task)
                      ? theme.palette.error.main
                      : TASK_PRIORITY_COLORS[task.priority],
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(237, 108, 2, 0.03)",
                    },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: { xs: 0.5, sm: 1 },
                  }}
                  onClick={() => navigate("/tasks")}
                >
                  <Checkbox
                    checked={false}
                    onChange={(e) => handleStatusToggle(task, e)}
                    onClick={(e) => e.stopPropagation()}
                    icon={<RadioButtonUnchecked />}
                    checkedIcon={<CheckCircle color="success" />}
                    sx={{
                      p: { xs: 0.25, sm: 0.5 },
                      mt: -0.5,
                      "&:hover": {
                        backgroundColor: "rgba(76, 175, 80, 0.08)",
                      },
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {task.title}
                        </Typography>
                        {task.dueDate && (
                          <Typography
                            variant="caption"
                            color={isOverdue(task) ? "error" : "text.secondary"}
                            fontWeight={isOverdue(task) ? 600 : 400}
                            sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                          >
                            {isOverdue(task) ? "⚠️ Vencida: " : "📅 "}
                            {new Date(task.dueDate).toLocaleDateString("es-ES")}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={
                          task.priority === "urgent"
                            ? "Urgente"
                            : task.priority === "high"
                              ? "Alta"
                              : task.priority === "medium"
                                ? "Media"
                                : "Baja"
                        }
                        size="small"
                        sx={{
                          fontSize: { xs: "0.55rem", sm: "0.65rem" },
                          height: { xs: 20, sm: 22 },
                          backgroundColor: TASK_PRIORITY_COLORS[task.priority],
                          color: "white",
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        <Button
          endIcon={<ArrowForward />}
          onClick={() => navigate("/tasks")}
          sx={{
            mt: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "12px",
            fontSize: { xs: "0.75rem", sm: "0.85rem" },
          }}
          fullWidth
        >
          Ver todas mis tareas
        </Button>
      </CardContent>
    </Card>
  );
};

export default PendingTasks;
